import { Collapse, Grid, styled } from "@mui/material";
import { memo, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import AlterIcon from "@icons-rf/alterestate-icon.svg?react";
import MxFormL3CheckField from "@rf-l3/form/MxFormL3CheckBoxField";
import MxIcons from "@rf-l1/MxIcons";
import MxFormSubTitleAndDescription from "@rf-l1/page/form/MxFormSubTitleAndDescription";
import MxFormL3InputField from "@rf-l3/form/MxFormL3InputField";

import { CPropertyFormKeys } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import useFormWatch from "@rf-hooks/useFormWatch";

/**
 * Componente que gestiona las opciones de publicación en AlterEstate
 *
 * @returns JSX.Element
 */
const AlterEstate = () => {
  const { setValue } = useFormContext();

  // Utilizamos un hook personalizado para observar los valores (mejor rendimiento)
  const watchedValues = useFormWatch([
    CPropertyFormKeys.want_publish_on_alterestate,
    CPropertyFormKeys.want_publish_on_internal_alterestate
  ]);

  // Extraemos los valores con nombres descriptivos
  const {
    want_publish_on_alterestate: publishOnAlterEstate,
    want_publish_on_internal_alterestate: publishOnInternalAlterEstate
  } = watchedValues;

  // Desactivamos la publicación interna cuando se desactiva la publicación principal
  useEffect(() => {
    if (!publishOnAlterEstate) {
      setValue(CPropertyFormKeys.want_publish_on_internal_alterestate, false, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [publishOnAlterEstate, setValue]);

  // Memoizamos el header del componente
  const headerSection = useMemo(
    () => (
      <Grid container item xs={12} alignItems="center">
        <Grid item flex={1}>
          <MxFormSubTitleAndDescription
            label="AlterEstate"
            description="Gestiona la publicación de esta propiedad en AlterEstate"
            isRequired={false}
          />
        </Grid>
        <Grid item>
          <MxIcons size={32}>
            <AlterIcon />
          </MxIcons>
        </Grid>
      </Grid>
    ),
    []
  );

  return (
    <CardAlterEstate container>
      {headerSection}

      <Grid container spacing={2} mt={0.5} item xs={12}>
        <Grid item xs={12}>
          <MxFormL3CheckField
            withreacthookform={1}
            label="¿Desea publicar esta propiedad en AlterEstate?"
            name={CPropertyFormKeys.want_publish_on_alterestate}
          />
        </Grid>

        {/* Solo renderizamos los componentes anidados cuando es necesario */}
        {publishOnAlterEstate && (
          <Grid item xs={12}>
            <MxFormL3CheckField
              withreacthookform={1}
              label="¿Desea publicar esta propiedad en la red interna de AlterEstate?"
              name={CPropertyFormKeys.want_publish_on_internal_alterestate}
            />
          </Grid>
        )}

        {publishOnAlterEstate && publishOnInternalAlterEstate && (
          <Grid item xs={12}>
            <MxFormL3InputField
              withreacthookform={1}
              label="¿Cuál es la comisión de esta propiedad?"
              name={CPropertyFormKeys.garante_commission_alterestate}
              placeholder="Ej: 5%"
              helperText="Introduzca el porcentaje de comisión"
            />
          </Grid>
        )}
      </Grid>
    </CardAlterEstate>
  );
};

// Componente estilizado para el contenedor principal
const CardAlterEstate = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

// Memoizamos el componente para evitar renderizados innecesarios
export default memo(AlterEstate);
